import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Hand, Clock, Zap } from 'lucide-react';

// Standard reactions - tile badge only (+1 point)
const STANDARD_REACTIONS = [
  { id: 'clap', emoji: '👏', label: 'Clap', points: 1 },
  { id: 'thumbs_up', emoji: '👍', label: 'Thumbs Up', points: 1 },
  { id: 'heart', emoji: '❤️', label: 'Heart', points: 1 },
  { id: 'laugh', emoji: '😂', label: 'Laugh', points: 1 },
  { id: 'wow', emoji: '😮', label: 'Wow', points: 1 },
  { id: 'celebrate', emoji: '🎉', label: 'Celebrate', points: 1 },
];

// Boosted reactions - tile badge + floating animation (+3 points)
const BOOSTED_REACTIONS = [
  { id: 'rocket', emoji: '🚀', label: 'Rocket', points: 3, boosted: true },
  { id: 'boom', emoji: '💥', label: 'Boom', points: 3, boosted: true },
  { id: 'fire', emoji: '🔥', label: 'Fire', points: 3, boosted: true },
];

// Legacy effect reactions for full-screen animations
const EFFECT_REACTIONS = [
  { id: 'balloon', emoji: '🎈', label: 'Balloon' },
  { id: 'confetti', emoji: '🎉', label: 'Confetti' },
  { id: 'hearts', emoji: '❤️', label: 'Hearts' },
  { id: 'applause', emoji: '👏', label: 'Applause' },
  { id: 'mind_blown', emoji: '🤯', label: 'Mind Blown' },
];

export default function ReactionsPanel({
  isOpen,
  onClose,
  onTriggerEffect,
  onSendReaction,
  onRaiseHand,
  onToggleBRB,
  isHandRaised = false,
  isBRB = false,
  reactionsEnabled = true,
  boostedReactionsEnabled = true,
  allowRaiseHand = true,
  disabledMessage = "Reactions are turned off by the host for this session."
}) {
  const [lastReactionTime, setLastReactionTime] = useState(0);
  const COOLDOWN_MS = 500;

  const handleReaction = (reaction) => {
    const now = Date.now();
    if (now - lastReactionTime < COOLDOWN_MS) return;

    setLastReactionTime(now);
    if (onSendReaction) {
      onSendReaction(reaction.id, reaction.emoji, reaction.boosted || false, reaction.points || 1);
    }
    if (onClose) onClose();
  };

  // Use portal to render at body level to escape z-index stacking context
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="reactions-panel-portal fixed bottom-20 left-4 right-4 mx-auto bg-[#001233] border border-white/20 rounded-2xl shadow-2xl z-[9999] max-w-[320px] max-h-[70vh] overflow-y-auto"
        >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="text-white font-medium text-sm">Reactions</span>
          <button
            onClick={() => onClose && onClose()}
            className="p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-3 space-y-4">
          {/* Emoji Reactions - only shown when reactions are enabled */}
          {reactionsEnabled && (
            <>
              {/* Standard Reactions */}
              <div>
                <span className="text-[11px] text-gray-400 uppercase tracking-wider mb-2 block">Reactions</span>
                <div className="flex items-center justify-between bg-white/5 rounded-xl p-2">
                  {STANDARD_REACTIONS.map((reaction) => (
                    <button
                      key={reaction.id}
                      onClick={() => handleReaction(reaction)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors group relative"
                      title={`${reaction.label} (+${reaction.points} pt)`}
                    >
                      <span className="text-xl group-hover:scale-125 transition-transform inline-block">
                        {reaction.emoji}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Boosted Reactions */}
              {boostedReactionsEnabled && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-3.5 h-3.5 text-purple-400" />
                    <span className="text-[11px] text-gray-400 uppercase tracking-wider">Boosted (+3 pts)</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-2 border border-purple-500/20">
                    {BOOSTED_REACTIONS.map((reaction) => (
                      <button
                        key={reaction.id}
                        onClick={() => handleReaction(reaction)}
                        className="flex-1 flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-white/10 transition-colors group"
                        title={`${reaction.label} (+${reaction.points} pts)`}
                      >
                        <span className="text-2xl group-hover:scale-125 transition-transform inline-block">
                          {reaction.emoji}
                        </span>
                        <span className="text-[9px] text-purple-300">{reaction.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Full-Screen Effects */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-[11px] text-gray-400 uppercase tracking-wider">Effects</span>
                </div>
                <div className="grid grid-cols-5 gap-1">
                  {EFFECT_REACTIONS.map((reaction) => (
                    <button
                      key={reaction.id}
                      onClick={() => {
                        if (onTriggerEffect) onTriggerEffect(reaction.id);
                        if (onClose) onClose();
                      }}
                      className="flex flex-col items-center gap-0.5 p-2 rounded-xl hover:bg-white/10 transition-colors group"
                    >
                      <span className="text-xl group-hover:scale-125 transition-transform">
                        {reaction.emoji}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Status Section - always shown */}
          <div>
            <span className="text-[11px] text-gray-400 uppercase tracking-wider mb-2 block">Status</span>
            <div className={`grid gap-2 ${allowRaiseHand ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {allowRaiseHand && (
                <button
                  onClick={() => {
                    if (onRaiseHand) onRaiseHand();
                    if (onClose) onClose();
                  }}
                  className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition-colors ${
                    isHandRaised
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Hand className="w-5 h-5" />
                  <span className="text-xs font-medium">
                    {isHandRaised ? 'Lower' : 'Raise Hand'}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  if (onToggleBRB) onToggleBRB();
                  if (onClose) onClose();
                }}
                className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition-colors ${
                  isBRB
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <Clock className="w-5 h-5" />
                <span className="text-xs font-medium text-center leading-tight">
                  {isBRB ? "I'm Back" : 'Be Right Back'}
                </span>
              </button>
            </div>
          </div>
        </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export { EFFECT_REACTIONS, STANDARD_REACTIONS, BOOSTED_REACTIONS };
